import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

// *** components *** //
import FormEntry from '../../pages/UserAccessReview/components/FormEntry';

// *** utils *** //
import { ADDRESS_FORM_TYPE, getFormTemplate } from './utils';
import { deepCloneObject } from '../../store/utility';

const AddressForm = ({
	defaultValues,
	fieldValidations,
	formType,
	onUpdate,
	onValidate,
	options,
	shouldFieldsBeReadOnly,
}) => {
	const defaultFormConfig = getFormTemplate(formType);
	const [form, setForm] = useState(defaultFormConfig);
	const [hasSetDefaultValues, setHasSetDefaultValues] = useState(false);

	// *** lifecycle *** //
	useEffect(() => {
		// make sure we only set default values once.. if not, then it will
		// keep setting them on each render making the fields uneditable
		if (!hasSetDefaultValues) {
			const newForm = { ...form };
			for (const key in defaultValues) {
				if (key in newForm) {
					newForm[key].value = defaultValues[key];
				}
			}
			setForm(deepCloneObject(newForm));
			setHasSetDefaultValues(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [defaultValues]);

	useEffect(() => {
		const newForm = { ...form };
		for (const key in options) {
			if (key in newForm) {
				newForm[key].options = options[key] || [];
			}
		}
		setForm(deepCloneObject(newForm));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [options]);

	useEffect(() => {
		const newForm = { ...form };
		for (const key in fieldValidations) {
			if (key in newForm) {
				newForm[key].validation = {
					...newForm[key].validation,
					...fieldValidations[key],
				};
			}
		}
		setForm(deepCloneObject(newForm));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fieldValidations]);

	/**
	 * Handler for event changes and allows us to update specific
	 * fields based on other field inputs if need be.
	 */
	const handleChange = key => event => {
		if (key in form) {
			let newForm = { ...form };
			newForm[key].value = event.target.value;

			setForm(newForm);
			onUpdate(newForm);
		}
	};

	const validate = () => {
		onUpdate(form);
		onValidate();
	};

	const generateFormInput = key => {
		let formInput = null;
		if (key in form && form[key].visible) {
			formInput = (
				<FormEntry
					entryKey={key}
					formDetails={form[key]}
					handleChange={handleChange(key)}
					validateChange={validate}
					inputType={
						form[key].settings && form[key].settings.type
							? form[key].settings.type
							: 'text'
					}
					readOnly={
						(form[key].settings && form[key].settings.readOnly) ||
						shouldFieldsBeReadOnly
					}
				/>
			);
		}
		return formInput;
	};

	const renderOfficeAddressForm = () => (
		<>
			{generateFormInput('street1')}
			{generateFormInput('street2')}
			{generateFormInput('street3')}
			{generateFormInput('country')}
			{generateFormInput('city')}
			{generateFormInput('state')}
			{generateFormInput('zipCode')}
		</>
	);

	const renderInternationalAddressForm = () => (
		<>
			{generateFormInput('street')}
			{generateFormInput('country')}
			{generateFormInput('city')}
			{generateFormInput('state')}
			{generateFormInput('zipCode')}
		</>
	);

	const renderUsAddressForm = () => (
		<>
			{generateFormInput('street')}
			{generateFormInput('city')}
			{generateFormInput('state')}
			{generateFormInput('zipCode')}
		</>
	);

	const renderForm = () => {
		switch (formType) {
			case ADDRESS_FORM_TYPE.INTERNATIONAL:
				return renderInternationalAddressForm();
			case ADDRESS_FORM_TYPE.US:
				return renderUsAddressForm();
			case ADDRESS_FORM_TYPE.OFFICE:
				return renderOfficeAddressForm();
			default:
				return null;
		}
	};

	return renderForm();
};

AddressForm.propTypes = {
	defaultValues: PropTypes.object,
	fieldValidations: PropTypes.object,
	formType: PropTypes.oneOf([
		ADDRESS_FORM_TYPE.OFFICE,
		ADDRESS_FORM_TYPE.INTERNATIONAL,
		ADDRESS_FORM_TYPE.US,
	]).isRequired,
	onUpdate: PropTypes.func.isRequired,
	onValidate: PropTypes.func.isRequired,
	options: PropTypes.shape({
		// can be updated in the future for other things we might need to pass
		country: PropTypes.array,
		state: PropTypes.array,
	}),
	shouldFieldsBeReadOnly: PropTypes.bool,
};

export default AddressForm;
